import type { Observable } from 'rxjs';

import { createProviderToken } from '@core/common-legacy';
import type { ConversationStartersContext } from '@core/tellent-assistant-types';

/**
 * Host-provided observable of the current context used to fetch
 * conversation starters for the assistant's empty state.
 *
 * Emit `null` when no context is available; the logic layer defaults
 * to `{ type: 'recruitment_general' }`.
 */
export const {
  provide: provideTellentConversationStartersContext,
  inject: injectTellentConversationStartersContext,
} = createProviderToken<Observable<ConversationStartersContext | null>>(
  'TELLENT_CONVERSATION_STARTERS_CONTEXT',
)(false);

/** Where a context originates: page-level vs. an overlay on top of it. */
export type ConversationStartersContextPriority = 'page' | 'overlay';

/**
 * Host-provided writer that lets feature libraries register the current
 * assistant context. `push` returns a cleanup function that removes the
 * registered context again (call it on component destroy).
 */
export interface ConversationStartersContextWriter {
  push: (
    context: ConversationStartersContext,
    priority: ConversationStartersContextPriority,
  ) => () => void;
}

export const {
  provide: provideTellentConversationStartersContextWriter,
  inject: injectTellentConversationStartersContextWriter,
} = createProviderToken<ConversationStartersContextWriter>(
  'TELLENT_CONVERSATION_STARTERS_CONTEXT_WRITER',
)(false);
